import { AlertCircle, ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import type { IconName } from '../../../components/IconSelector';
import IconSelector from '../../../components/IconSelector';
import type {
  CreateShopItemPayload,
  UpdateShopItemPayload,
} from '../../../service/api';
import api from '../../../service/api';

type CreateShopItemFormData = Omit<CreateShopItemPayload, 'price'> & {
  price: number | null;
  purchaseType: 'standard' | 'collectable';
};

export default function CreateShopItemPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: itemId } = useParams<{ id: string }>();
  const isEditMode = !!itemId;

  const [isLoadingItem, setIsLoadingItem] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<CreateShopItemFormData>({
    title: '',
    description: '',
    price: 0,
    icon: 'shopping-bag',
    purchaseType: 'standard',
  });

  useEffect(() => {
    if (isEditMode) {
      fetchItem();
    }
    // eslint-disable-next-line
  }, [isEditMode, itemId]);

  const fetchItem = async () => {
    if (!itemId) return;
    setIsLoadingItem(true);
    try {
      const item = await api.shopItemGetById(itemId);
      setFormData({
        title: item.title,
        description: item.description || '',
        price: item.price,
        icon: item.icon,
        purchaseType: item.purchaseType || 'standard',
      });
    } catch {
      setError('Failed to load shop item');
    } finally {
      setIsLoadingItem(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const resolvedPrice = formData.price ?? 1;

      if (isEditMode) {
        const updateData: UpdateShopItemPayload = {};
        if (formData.title) updateData.title = formData.title;
        if (formData.description !== undefined)
          updateData.description = formData.description;
        if (formData.price != null) updateData.price = resolvedPrice;
        if (formData.icon) updateData.icon = formData.icon;
        updateData.purchaseType = formData.purchaseType;

        await api.shopItemUpdate(itemId!, updateData);
      } else {
        const payload: CreateShopItemPayload = {
          title: formData.title,
          description: formData.description,
          price: resolvedPrice,
          icon: formData.icon,
          purchaseType: formData.purchaseType,
        };
        await api.shopItemCreate(payload);
      }
      navigate('/manager/shop');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? 'update' : 'create'} shop item`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIconChange = (icon: IconName) => {
    setFormData({ ...formData, icon });
  };

  const handleDelete = async () => {
    if (!itemId || !window.confirm(t('manager.shop.confirmDelete'))) return;

    setIsSubmitting(true);
    try {
      await api.shopItemDelete(itemId);
      navigate('/manager/shop');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete shop item',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white min-vh-100">
      <Container fluid>
        <Row className="align-items-center bg-white p-4 pb-2">
          <Col xs="auto">
            <Button
              variant="link"
              className="p-0 text-dark"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft size={24} />
            </Button>
          </Col>
          <Col>
            <h2 className="h5 fw-bold mb-0">
              {isEditMode
                ? t('manager.shop.editItem')
                : t('manager.shop.createItem')}
            </h2>
          </Col>
        </Row>

        {isLoadingItem ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          <Form onSubmit={handleSubmit} className="p-4">
            {error && (
              <Alert variant="danger" className="mb-4">
                <AlertCircle size={16} className="me-2" />
                {error}
              </Alert>
            )}

            <h6 className="fw-bold mb-3">{t('manager.shop.itemDetails')}</h6>

            <Form.Group className="mb-3">
              <Form.Label>{t('manager.shop.titleLabel')} *</Form.Label>
              <Form.Control
                type="text"
                value={formData.title}
                onChange={e =>
                  setFormData({ ...formData, title: e.target.value })
                }
                required
                placeholder={t('manager.shop.titlePlaceholder')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('manager.shop.descriptionLabel')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder={t('manager.shop.descriptionPlaceholder')}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {t('manager.shop.priceLabel')} ({t('common.stickers')}) *
              </Form.Label>
              <Form.Control
                type="number"
                min="1"
                step="1"
                value={formData.price ?? ''}
                onChange={e => {
                  const rawValue = e.target.value;
                  const value =
                    rawValue === ''
                      ? null
                      : Math.max(1, parseInt(rawValue, 10) || 1);
                  setFormData({
                    ...formData,
                    price: value,
                  });
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>{t('manager.shop.iconLabel')}</Form.Label>
              <IconSelector
                value={formData.icon || 'shopping-bag'}
                onChange={handleIconChange}
                className="w-100"
              />
            </Form.Group>

            {/* Purchase type toggle - only shown on create (immutable after creation) */}
            {!isEditMode && (
              <Form.Group className="mb-4">
                <Form.Label>{t('manager.shop.purchaseTypeLabel')}</Form.Label>
                <div className="d-flex gap-2">
                  <Button
                    variant={
                      formData.purchaseType === 'standard'
                        ? 'primary'
                        : 'outline-primary'
                    }
                    className="flex-grow-1"
                    onClick={() =>
                      setFormData({ ...formData, purchaseType: 'standard' })
                    }
                    type="button"
                  >
                    {t('manager.shop.purchaseTypeStandard')}
                  </Button>
                  <Button
                    variant={
                      formData.purchaseType === 'collectable'
                        ? 'info'
                        : 'outline-info'
                    }
                    className="flex-grow-1"
                    onClick={() =>
                      setFormData({ ...formData, purchaseType: 'collectable' })
                    }
                    type="button"
                  >
                    {t('manager.shop.purchaseTypeCollectable')}
                  </Button>
                </div>
                <Form.Text className="text-muted">
                  {formData.purchaseType === 'standard'
                    ? t('manager.shop.purchaseTypeStandardHelp')
                    : t('manager.shop.purchaseTypeCollectableHelp')}
                </Form.Text>
              </Form.Group>
            )}

            <div className="d-flex gap-2">
              <Button
                variant="secondary"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </Button>
              {isEditMode && (
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  disabled={isSubmitting}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={
                  isSubmitting ||
                  !formData.title ||
                  formData.price == null ||
                  formData.price < 1
                }
                className="flex-grow-1"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isEditMode
                      ? t('manager.shop.updating')
                      : t('manager.shop.creating')}
                  </>
                ) : isEditMode ? (
                  t('common.update')
                ) : (
                  t('common.create')
                )}
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </div>
  );
}
